import React, { useEffect, useMemo, useState } from 'react';
import { Search, ExternalLink, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { setSeoMetadata } from '../lib/seo';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Public showcase directory — every published project, with search and
 * category filtering.
 *
 * Filtering happens client-side on purpose: the directory is a few dozen
 * entries at most, so a round trip per keystroke would be slower and more
 * fragile than filtering an array already in memory. If it ever grows past a
 * few hundred, move the query server-side.
 */
const ShowcaseDirectoryPage = () => {
  const flags = useSiteFeatureFlags();
  const { siteName } = useSiteSettings();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Two independent axes. A project is both a sector and a build type, so
  // these narrow together rather than replacing one another.
  const [industry, setIndustry] = useState('all');
  const [projectType, setProjectType] = useState('all');
  const [options, setOptions] = useState({ industry: [], project_type: [] });

  const enabled = flags._loaded ? flags.showcase_enabled === true : true;

  useEffect(() => {
    setSeoMetadata({
      title: 'Our Work',
      description: `Applications and platforms built by ${siteName || 'our team'} — custom software, CRM systems, e-commerce and AI tools.`,
      canonicalPath: '/apps',
    });
  }, [siteName]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Projects and filter options together — the page is not usable
        // without both, so there is nothing to show by resolving them apart.
        const [projectsRes, filtersRes] = await Promise.all([
          fetch(`${backendUrl}/api/public/showcase/`),
          fetch(`${backendUrl}/api/public/showcase/filters`),
        ]);
        if (!projectsRes.ok) throw new Error(`HTTP ${projectsRes.status}`);

        const data = await projectsRes.json();
        if (active) setProjects(Array.isArray(data) ? data : []);

        // Filters failing is survivable: search still works without them.
        if (filtersRes.ok) {
          const f = await filtersRes.json();
          if (active) {
            setOptions({
              industry: Array.isArray(f.industry) ? f.industry : [],
              project_type: Array.isArray(f.project_type) ? f.project_type : [],
            });
          }
        }
      } catch (err) {
        if (active) setProjects([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /**
   * Only offer options that actually match something.
   *
   * Showing every configured filter would let a visitor pick one and land on
   * an empty grid, which reads as a broken page rather than an empty category.
   */
  const available = useMemo(() => {
    // Each project carries several values per axis, so flatten before counting
    // what is actually in use.
    const usedIndustries = new Set(projects.flatMap((p) => p.industries || []));
    const usedTypes = new Set(projects.flatMap((p) => p.project_types || []));
    return {
      industry: options.industry.filter((o) => usedIndustries.has(o.name)),
      project_type: options.project_type.filter((o) => usedTypes.has(o.name)),
    };
  }, [projects, options]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      // A project matches if ANY of its values matches the chosen filter.
      if (industry !== 'all' && !(p.industries || []).includes(industry)) return false;
      if (projectType !== 'all' && !(p.project_types || []).includes(projectType)) return false;
      if (!q) return true;
      // Search across everything a visitor might reasonably type, including
      // the feature list — people search for capabilities, not just names.
      const haystack = [
        p.name,
        p.tagline,
        p.short_description,
        p.category,
        ...(p.industries || []),
        ...(p.project_types || []),
        ...(p.features || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [projects, query, industry, projectType]);

  const filtersActive = industry !== 'all' || projectType !== 'all' || query.trim() !== '';

  const clearAll = () => {
    setQuery('');
    setIndustry('all');
    setProjectType('all');
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />

      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
            OUR PORTFOLIO
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Applications we have{' '}
            <span className="text-[#FF6B00]">built and shipped.</span>
          </h1>
          <p className="text-[#dfddd6] text-lg max-w-2xl">
            Real systems running in production for real businesses. Every one of these started
            as a conversation about scope.
          </p>
        </div>
      </section>

      {!enabled ? (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-[#888680]">The portfolio is not available right now.</p>
          </div>
        </section>
      ) : (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4">
            {/* Controls */}
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
              <div className="relative w-full lg:max-w-sm">
                <Search className="w-4 h-4 text-[#888680] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search projects, features, categories…"
                  aria-label="Search the portfolio"
                  data-testid="showcase-search"
                  className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-[#302f2c] border border-[#4a3520] text-white placeholder-[#888680] text-sm focus:border-[#FF6B00] outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888680] hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {filtersActive && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#4a3520] text-[#888680] text-xs hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
                  data-testid="showcase-clear"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear filters
                </button>
              )}
            </div>

            {/* Two filter axes. Rendered as separate labelled rows rather than
                one merged list, so it is obvious they narrow together. */}
            <div className="space-y-3 mb-8">
              {[
                { key: 'industry', label: 'Industry', value: industry, set: setIndustry },
                { key: 'project_type', label: 'Type', value: projectType, set: setProjectType },
              ].map((axis) =>
                available[axis.key].length > 0 ? (
                  <div key={axis.key} className="flex flex-wrap items-center gap-2">
                    <span className="text-[#888680] text-xs font-semibold uppercase tracking-wide w-16 shrink-0">
                      {axis.label}
                    </span>
                    <div
                      className="flex flex-wrap gap-2"
                      role="group"
                      aria-label={`Filter by ${axis.label.toLowerCase()}`}
                    >
                      {['all', ...available[axis.key].map((o) => o.name)].map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => axis.set(name)}
                          aria-pressed={axis.value === name}
                          data-testid={`filter-${axis.key}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            axis.value === name
                              ? 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]'
                              : 'border-[#4a3520] text-[#dfddd6] hover:border-[#888680]'
                          }`}
                        >
                          {name === 'all' ? 'All' : name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
            </div>

            {/* Result count — tells people the filter did something, which is
                otherwise ambiguous when results simply disappear. */}
            {!loading && (
              <p className="text-[#888680] text-sm mb-6" aria-live="polite" data-testid="showcase-count">
                {visible.length} {visible.length === 1 ? 'project' : 'projects'}
                {industry !== 'all' && ` in ${industry}`}
                {projectType !== 'all' && ` · ${projectType}`}
                {query && ` matching “${query}”`}
              </p>
            )}

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-[#4a3520] bg-[#302f2c] p-6 h-56 animate-pulse"
                  />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-white text-lg mb-2">Nothing matches that.</p>
                <p className="text-[#888680] text-sm mb-6">
                  Try a different search, or clear the filters.
                </p>
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-4 py-2 rounded-lg border border-[#4a3520] text-[#dfddd6] text-sm hover:border-[#FF6B00] hover:text-[#FF6B00]"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visible.map((p) => (
                  <article
                    key={p.id}
                    className="group rounded-2xl border border-[#4a3520] bg-[#302f2c] overflow-hidden hover:border-[#FF6B00] transition-colors flex flex-col"
                    data-testid={`showcase-card-${p.slug}`}
                  >
                    {p.screenshots?.[0] && (
                      <img
                        src={resolveMediaUrl(p.screenshots[0])}
                        alt=""
                        loading="lazy"
                        className="w-full h-40 object-cover object-top border-b border-[#4a3520]"
                      />
                    )}

                    <div className="p-6 flex flex-col flex-1">
                      {p.category && (
                        <span className="text-[#888680] text-xs font-semibold tracking-wide uppercase mb-2">
                          {p.category}
                        </span>
                      )}
                      <h2 className="text-white text-lg font-bold mb-1">{p.name}</h2>
                      {p.tagline && <p className="text-[#FF6B00] text-sm mb-3">{p.tagline}</p>}
                      {p.short_description && (
                        <p className="text-[#888680] text-sm leading-relaxed mb-4">
                          {p.short_description}
                        </p>
                      )}

                      {Array.isArray(p.features) && p.features.length > 0 && (
                        <ul className="space-y-1.5 mb-5">
                          {p.features.slice(0, 3).map((f) => (
                            <li key={f} className="text-[#dfddd6] text-sm flex items-start gap-2">
                              <span className="text-[#FF6B00] mt-0.5">&bull;</span>
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}

                      {p.url && (
                        <div className="mt-auto pt-2">
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-white group-hover:text-[#FF6B00] transition-colors text-sm font-semibold"
                          >
                            Visit site
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ShowcaseDirectoryPage;
